import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type Theme = 'light' | 'dark' | 'system';

interface UIState {
  sidebarCollapsed: boolean;
  commandPaletteOpen: boolean;
  searchOpen: boolean;
  startMenuOpen: boolean;
  aiPanelOpen: boolean;
  aiPanelExpanded: boolean;
  aiPanelShowHistory: boolean;
  /** Panel width in px when not expanded; persisted across sessions. */
  aiPanelWidth: number;
  /** Recent-chats history drawer (right side) on the full AI CIO page. */
  aiCioPageHistoryOpen: boolean;
  /**
   * Width (px) of the currently-open back panel (detail drawer / side panel).
   * Set to 0 when no back panel is open. Used by the AI CIO panel to expand
   * wide enough to fully cover the back panel without closing it.
   */
  backPanelWidth: number;
  /**
   * True when a back panel (detail drawer / side panel) was opened more
   * recently than the AI CIO panel — it then renders ABOVE the AI CIO panel
   * instead of being covered by it. Whichever surface the user opened last
   * is the one they're trying to look at. Flipped back to false whenever the
   * AI CIO panel itself is (re)opened.
   */
  backPanelFront: boolean;
  /**
   * Width (px) the AI CIO panel keeps once a back panel that had widened it
   * closes — it stays at the back panel's width instead of snapping back
   * down to `aiPanelWidth`. 0 when no such carry-over is in effect. Reset by
   * a manual resize drag (which always wins) or by closing the AI CIO panel
   * outright — a fresh open uses the plain saved `aiPanelWidth` again.
   */
  aiPanelExpandedWidth: number;
  /**
   * Bumped whenever the user asks for a brand-new AI CIO chat from the app
   * shell (sidebar logo / Start menu). The AI CIO module watches this counter
   * and starts a fresh chat — a boundary-safe seam (shared cannot import the
   * AI CIO store directly).
   */
  aiCioNewChatNonce: number;
  theme: Theme;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  requestNewAiCioChat: () => void;
  toggleCommandPalette: () => void;
  toggleSearch: () => void;
  toggleStartMenu: () => void;
  setStartMenuOpen: (v: boolean) => void;
  openAiPanel: () => void;
  closeAiPanel: () => void;
  toggleAiPanel: () => void;
  setAiPanelExpanded: (v: boolean) => void;
  setAiPanelShowHistory: (v: boolean) => void;
  setAiPanelWidth: (px: number) => void;
  setAiCioPageHistoryOpen: (v: boolean) => void;
  setBackPanelWidth: (px: number) => void;
  setTheme: (theme: Theme) => void;
}

/**
 * Default floating-panel width in px. Per the Figma "AI CIO" frame the panel
 * opens narrow (~331px on a 1440 canvas) and can be dragged wider up to the
 * 1/3-viewport cap — so the default MUST sit below that cap to leave drag room.
 * (The old 480px default equalled the cap on a 1440 screen → no room to widen.)
 */
export const AI_PANEL_DEFAULT_WIDTH = 360;
/** Hard cap: the panel may not exceed one third of the viewport (per spec). */
export const AI_PANEL_MAX_FRACTION = 1 / 3;

/** Largest allowed panel width for the current viewport (one third of it). */
export function maxAiPanelWidth(): number {
  if (typeof window === 'undefined') return AI_PANEL_DEFAULT_WIDTH;
  return Math.round(window.innerWidth * AI_PANEL_MAX_FRACTION);
}

/**
 * Clamps a desired panel width to [default, 1/3-viewport]. On very narrow
 * viewports where the default already exceeds the cap, the cap wins so the
 * panel never spills past one third of the screen.
 */
export function clampAiPanelWidth(px: number): number {
  const max = maxAiPanelWidth();
  const min = Math.min(AI_PANEL_DEFAULT_WIDTH, max);
  return Math.min(Math.max(px, min), max);
}

/**
 * Actual rendered width of the AI CIO panel. It grows past the saved width to
 * cover an open back panel (`backPanelWidth`) and keeps that width afterwards
 * (`aiPanelExpandedWidth`); a manual drag still wins when it is wider.
 */
export function resolveAiPanelWidth(
  aiPanelWidth: number,
  backPanelWidth: number,
  aiPanelExpandedWidth: number,
): number {
  return Math.max(clampAiPanelWidth(aiPanelWidth), backPanelWidth, aiPanelExpandedWidth);
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      commandPaletteOpen: false,
      searchOpen: false,
      startMenuOpen: false,
      aiPanelOpen: false,
      aiPanelExpanded: false,
      aiPanelShowHistory: false,
      aiPanelWidth: AI_PANEL_DEFAULT_WIDTH,
      aiCioPageHistoryOpen: false,
      aiCioNewChatNonce: 0,
      backPanelWidth: 0,
      backPanelFront: false,
      aiPanelExpandedWidth: 0,
      theme: 'light',

      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      requestNewAiCioChat: () => set((s) => ({ aiCioNewChatNonce: s.aiCioNewChatNonce + 1 })),
      toggleCommandPalette: () => set((s) => ({ commandPaletteOpen: !s.commandPaletteOpen })),
      toggleSearch: () => set((s) => ({ searchOpen: !s.searchOpen })),
      toggleStartMenu: () => set((s) => ({ startMenuOpen: !s.startMenuOpen })),
      setStartMenuOpen: (v) => set({ startMenuOpen: v }),
      // Opening the AI CIO panel is a fresh user action — bring it back to
      // front even if a back panel had most recently claimed that spot.
      openAiPanel: () => set({ aiPanelOpen: true, backPanelFront: false }),
      closeAiPanel: () =>
        set({
          aiPanelOpen: false,
          aiPanelExpanded: false,
          aiPanelShowHistory: false,
          aiPanelExpandedWidth: 0,
        }),
      toggleAiPanel: () =>
        set((s) => ({
          aiPanelOpen: !s.aiPanelOpen,
          aiPanelExpanded: s.aiPanelOpen ? false : s.aiPanelExpanded,
          aiPanelShowHistory: s.aiPanelOpen ? false : s.aiPanelShowHistory,
          backPanelFront: s.aiPanelOpen ? s.backPanelFront : false,
          aiPanelExpandedWidth: s.aiPanelOpen ? 0 : s.aiPanelExpandedWidth,
        })),
      setAiPanelExpanded: (v) => set({ aiPanelExpanded: v }),
      setAiPanelShowHistory: (v) => set({ aiPanelShowHistory: v }),
      setAiPanelWidth: (px) => set({ aiPanelWidth: px }),
      setAiCioPageHistoryOpen: (v) => set({ aiCioPageHistoryOpen: v }),
      // A back panel opening (px > 0) is a fresh user action too — it claims
      // the front spot from the AI CIO panel until AI CIO is reopened. Closing
      // it (px === 0) hands its width off as the AI CIO panel's new floor, so
      // it stays at that width instead of snapping back down — but only when
      // the AI CIO panel was actually open (and thus covering it) at the
      // time; a back panel opening and closing while AI CIO was never part
      // of the picture must not make the NEXT fresh AI CIO open start wide.
      setBackPanelWidth: (px) =>
        set((s) => ({
          backPanelWidth: px,
          backPanelFront: px > 0 || s.backPanelFront,
          aiPanelExpandedWidth:
            px === 0 && s.aiPanelOpen
              ? Math.max(s.aiPanelExpandedWidth, s.backPanelWidth)
              : s.aiPanelExpandedWidth,
        })),
      setTheme: (theme) => set({ theme }),
    }),
    {
      name: 'investbanq-ui',
      version: 1,
      // v0 shipped a 480px default that equalled the 1/3 cap on a 1440 screen,
      // so a persisted width left no room to drag the panel wider. Reset stored
      // widths to the corrected narrower default on upgrade.
      migrate: (persisted) =>
        ({
          ...(persisted as Partial<UIState>),
          aiPanelWidth: AI_PANEL_DEFAULT_WIDTH,
        }) as UIState,
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
        aiPanelWidth: state.aiPanelWidth,
      }),
    },
  ),
);
